const DB_NAME = 'offline-datasets';
const STORE_NAME = 'datasets';
const METADATA_STORE_NAME = 'metadata';
const METADATA_TENANT_INDEX = 'tenantSlug';
const DB_VERSION = 2;

const DEFAULT_STORAGE_BUDGET_BYTES = 50 * 1024 * 1024;

export interface OfflineDatasetRecord<T = unknown> {
  tenantSlug: string;
  dataset: string;
  version: string;
  data: T;
  cachedAt: number;
}

const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

interface DatasetMetadata {
  tenantSlug: string;
  dataset: string;
  cachedAt: number;
  sizeBytes: number;
  lastAccessed: number;
}

const memoryStore = new Map<string, OfflineDatasetRecord>();
const metadataMemoryStore = new Map<string, DatasetMetadata>();
const memoryKey = (tenantSlug: string, dataset: string) => `${tenantSlug}::${dataset}`;

const resolveStorageBudget = (): number => {
  const fromMeta = typeof import.meta !== 'undefined' && import.meta.env?.VITE_OFFLINE_DATASET_BUDGET_BYTES;
  const fromGlobal = typeof globalThis !== 'undefined'
    ? (globalThis as typeof globalThis & { VITE_OFFLINE_DATASET_BUDGET_BYTES?: string }).VITE_OFFLINE_DATASET_BUDGET_BYTES
    : undefined;
  const candidate = Number.parseInt(fromMeta ?? fromGlobal ?? '', 10);
  return Number.isFinite(candidate) && candidate > 0 ? candidate : DEFAULT_STORAGE_BUDGET_BYTES;
};

const STORAGE_BUDGET_BYTES = resolveStorageBudget();

const openDatabase = async (): Promise<IDBDatabase | null> => {
  if (!isBrowser) {
    return null;
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: ['tenantSlug', 'dataset'] });
        store.createIndex('tenantDataset', ['tenantSlug', 'dataset'], { unique: true });
      }
      let metadataStore: IDBObjectStore | null = null;
      if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
        metadataStore = db.createObjectStore(METADATA_STORE_NAME, { keyPath: ['tenantSlug', 'dataset'] });
        metadataStore.createIndex('tenantDataset', ['tenantSlug', 'dataset'], { unique: true });
        metadataStore.createIndex(METADATA_TENANT_INDEX, 'tenantSlug', { unique: false });
      } else if (request.transaction) {
        try {
          metadataStore = request.transaction.objectStore(METADATA_STORE_NAME);
          if (metadataStore && !metadataStore.indexNames.contains(METADATA_TENANT_INDEX)) {
            metadataStore.createIndex(METADATA_TENANT_INDEX, 'tenantSlug', { unique: false });
          }
        } catch {
          /* ignore */
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'));
  });
};

const estimateRecordSize = (record: OfflineDatasetRecord): number => {
  try {
    const payload = JSON.stringify(record.data ?? null);
    return payload ? payload.length : 0;
  } catch {
    return 0;
  }
};

const persistMetadataMemory = (metadata: DatasetMetadata): void => {
  metadataMemoryStore.set(memoryKey(metadata.tenantSlug, metadata.dataset), metadata);
};

const updateMetadataAccessMemory = (tenantSlug: string, dataset: string): void => {
  const key = memoryKey(tenantSlug, dataset);
  const record = metadataMemoryStore.get(key);
  if (!record) {
    return;
  }
  record.lastAccessed = Date.now();
  metadataMemoryStore.set(key, record);
};

const readMetadataForTenant = async (tenantSlug: string): Promise<DatasetMetadata[]> => {
  if (!isBrowser) {
    return Array.from(metadataMemoryStore.values()).filter((entry) => entry.tenantSlug === tenantSlug);
  }
  const db = await openDatabase();
  if (!db) {
    return [];
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(METADATA_STORE_NAME, 'readonly');
    const index = tx.objectStore(METADATA_STORE_NAME).index(METADATA_TENANT_INDEX);
    const request = index.getAll(IDBKeyRange.only(tenantSlug));
    request.onsuccess = () => resolve((request.result as DatasetMetadata[]) ?? []);
    request.onerror = () => reject(request.error ?? new Error('metadata read failed'));
  });
};

const updateMetadataAccess = async (tenantSlug: string, dataset: string): Promise<void> => {
  if (!isBrowser) {
    updateMetadataAccessMemory(tenantSlug, dataset);
    return;
  }
  const db = await openDatabase();
  if (!db) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(METADATA_STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('metadata touch failed'));
    const store = tx.objectStore(METADATA_STORE_NAME);
    const request = store.get([tenantSlug, dataset]);
    request.onsuccess = () => {
      const record = request.result as DatasetMetadata | undefined;
      if (!record) {
        return;
      }
      record.lastAccessed = Date.now();
      store.put(record);
    };
  });
};

const deleteDatasetRecord = async (tenantSlug: string, dataset: string): Promise<void> => {
  if (!isBrowser) {
    memoryStore.delete(memoryKey(tenantSlug, dataset));
    metadataMemoryStore.delete(memoryKey(tenantSlug, dataset));
    return;
  }
  const db = await openDatabase();
  if (!db) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, METADATA_STORE_NAME], 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('dataset delete failed'));
    tx.objectStore(STORE_NAME).delete([tenantSlug, dataset]);
    tx.objectStore(METADATA_STORE_NAME).delete([tenantSlug, dataset]);
  });
};

const enforceTenantBudget = async (tenantSlug: string): Promise<void> => {
  const entries = await readMetadataForTenant(tenantSlug);
  if (!entries.length) {
    return;
  }
  let totalBytes = entries.reduce((sum, entry) => sum + entry.sizeBytes, 0);
  if (totalBytes <= STORAGE_BUDGET_BYTES) {
    return;
  }
  const targetBytes = STORAGE_BUDGET_BYTES * 0.9;
  const ordered = entries.sort((a, b) => (a.lastAccessed ?? a.cachedAt) - (b.lastAccessed ?? b.cachedAt));
  for (const entry of ordered) {
    await deleteDatasetRecord(entry.tenantSlug, entry.dataset);
    totalBytes -= entry.sizeBytes;
    if (totalBytes <= targetBytes) {
      break;
    }
  }
};

export const saveOfflineDataset = async <T>(record: OfflineDatasetRecord<T>): Promise<void> => {
  if (!isBrowser) {
    memoryStore.set(memoryKey(record.tenantSlug, record.dataset), record);
    persistMetadataMemory({
      tenantSlug: record.tenantSlug,
      dataset: record.dataset,
      cachedAt: record.cachedAt,
      sizeBytes: estimateRecordSize(record),
      lastAccessed: Date.now(),
    });
    return;
  }
  const db = await openDatabase();
  if (!db) {
    return;
  }
  const metadata: DatasetMetadata = {
    tenantSlug: record.tenantSlug,
    dataset: record.dataset,
    cachedAt: record.cachedAt,
    sizeBytes: estimateRecordSize(record),
    lastAccessed: Date.now(),
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, METADATA_STORE_NAME], 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('dataset save failed'));
    tx.objectStore(STORE_NAME).put(record);
    tx.objectStore(METADATA_STORE_NAME).put(metadata);
  });
  await enforceTenantBudget(record.tenantSlug);
};

export const readOfflineDataset = async <T>(
  tenantSlug: string,
  dataset: string,
): Promise<OfflineDatasetRecord<T> | null> => {
  if (!isBrowser) {
    const cached = memoryStore.get(memoryKey(tenantSlug, dataset)) as OfflineDatasetRecord<T> | undefined;
    if (cached) {
      updateMetadataAccessMemory(tenantSlug, dataset);
    }
    return cached ?? null;
  }
  const db = await openDatabase();
  if (!db) {
    return null;
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    tx.onerror = () => reject(tx.error ?? new Error('dataset read failed'));
    const request = tx.objectStore(STORE_NAME).get([tenantSlug, dataset]);
    request.onsuccess = () => {
      const result = request.result ?? null;
      if (result) {
        void updateMetadataAccess(tenantSlug, dataset);
      }
      resolve(result);
    };
    request.onerror = () => reject(request.error ?? new Error('dataset read failed'));
  });
};

export const clearOfflineDataset = async (tenantSlug: string, dataset: string): Promise<void> => {
  await deleteDatasetRecord(tenantSlug, dataset);
};
